import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext.jsx';
import { useSocket } from '../../hooks/useSocket.js';
import Logo    from '../../components/common/Logo.jsx';
import Badge   from '../../components/common/Badge.jsx';
import Button  from '../../components/common/Button.jsx';
import api     from '../../services/api.js';
import GridLayout from '../../components/viewer/GridLayout.jsx';
import RecordingTimeline from '../../components/viewer/RecordingTimeline.jsx';
import AudioControls from '../../components/viewer/AudioControls.jsx';
import BillingPage   from './BillingPage.jsx';
import AdminPage     from './AdminPage.jsx';
import SettingsPage    from './SettingsPage.jsx';
import RecordingsPage  from './RecordingsPage.jsx';
import { useElectron }       from '../../hooks/useElectron.js';
import OnboardingWizard      from '../../components/common/OnboardingWizard.jsx';
import AlertTimeline         from '../../components/viewer/AlertTimeline.jsx';

export default function Dashboard() {
  const { user, logout }      = useAuth();
  const { socket, connected } = useSocket();
  const { t }                 = useTranslation();
  const { isElectron, updateDownloaded, installUpdate } = useElectron();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/dashboard',              label: t('nav.cameras'),      icon: '📹' },
    { path: '/dashboard/alerts',       label: t('nav.alerts'),       icon: '🔔' },
    { path: '/dashboard/recordings',   label: t('nav.recordings'),   icon: '📼' },
    { path: '/dashboard/billing',      label: t('nav.billing'),      icon: '💳' },
    { path: '/dashboard/settings',     label: t('nav.settings'),     icon: '⚙️' },
    { path: '/camera',                 label: 'Mode Caméra',          icon: '📷' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--elvyra-bg)', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? 240 : 64, transition: 'width var(--transition-normal)',
        background: 'var(--elvyra-surface)', borderRight: '1px solid var(--elvyra-border)',
        display: 'flex', flexDirection: 'column', padding: 'var(--space-4)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
          {sidebarOpen && <Logo size="sm" showText={true} />}
          <button onClick={() => setSidebarOpen(o => !o)}
            style={{ background: 'none', border: 'none', color: 'var(--elvyra-text-muted)', cursor: 'pointer', fontSize: 18 }}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[
            ...navItems,
            ...(user?.role === 'ADMIN' ? [{ path: '/dashboard/admin', label: t('nav.admin'), icon: '🛡' }] : []),
          ].map(item => (
            <Link key={item.path} to={item.path}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                color: location.pathname === item.path ? 'var(--elvyra-accent)' : 'var(--elvyra-text-secondary)',
                background: location.pathname === item.path ? 'rgba(0,212,255,0.08)' : 'transparent',
                textDecoration: 'none', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
                transition: 'all var(--transition-fast)',
              }}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              {sidebarOpen && item.label}
            </Link>
          ))}
        </nav>

        <div style={{ borderTop: '1px solid var(--elvyra-border)', paddingTop: 'var(--space-4)' }}>
          {sidebarOpen && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-secondary)', fontWeight: 600 }}>{user?.name}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span style={{
                  fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 'var(--radius-full)',
                  background: 'rgba(0,212,255,0.1)', color: 'var(--elvyra-accent)',
                }}>{user?.plan}</span>
                <Badge status={connected ? 'online' : 'offline'} label={connected ? 'Connecté' : 'Hors ligne'} />
              </div>
            </div>
          )}
          <button onClick={handleLogout}
            style={{
              width: '100%', background: 'none', border: '1px solid var(--elvyra-border)',
              borderRadius: 'var(--radius-md)', padding: 'var(--space-2)',
              color: 'var(--elvyra-danger)', cursor: 'pointer', fontSize: 'var(--text-sm)',
              display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'flex-start' : 'center', gap: 6,
            }}>
            🚪 {sidebarOpen && t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, overflow: 'auto', padding: 'var(--space-6)', display: 'flex', flexDirection: 'column' }}>
        {updateDownloaded && (
          <div style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--elvyra-accent)', borderRadius: '8px', padding: '10px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--elvyra-accent)', fontSize: '13px' }}>🔄 Une mise à jour est prête — redémarrez pour l'installer.</span>
            <button onClick={installUpdate} style={{ background: 'var(--elvyra-accent)', color: '#060E1F', border: 'none', borderRadius: '6px', padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '12px' }}>Installer</button>
          </div>
        )}
        <div style={{ flex: 1 }}>
          <Routes>
            <Route index element={<CamerasPage socket={socket} />} />
            <Route path="alerts"     element={<AlertsPage />} />
            <Route path="recordings" element={<RecordingsPage />} />
            <Route path="billing"    element={<BillingPage />} />
            <Route path="admin"      element={<AdminPage />} />
            <Route path="settings"   element={<SettingsPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function CamerasPage({ socket }) {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModal, setAddModal] = useState(false);
  const [newDevice, setNewDevice] = useState({ name: '', location: '', groupName: '' });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showRecordings, setShowRecordings] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    api.get('/devices').then(r => {
      const devs = r.data.data || [];
      setDevices(devs);
      const seen = localStorage.getItem('elvyra_onboarded');
      if (!seen && devs.length === 0) setShowOnboarding(true);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('camera:online',  ({ streamKey }) => setDevices(d => d.map(x => x.streamKey === streamKey ? { ...x, status: 'ONLINE' } : x)));
    socket.on('camera:offline', ({ streamKey }) => setDevices(d => d.map(x => x.streamKey === streamKey ? { ...x, status: 'OFFLINE' } : x)));
    socket.on('motion:alert', ({ alert, deviceName }) => toast.error(`⚠ Mouvement — ${deviceName}`, { duration: 6000 }));
    return () => { socket.off('camera:online'); socket.off('camera:offline'); socket.off('motion:alert'); };
  }, [socket]);

  const addDevice = async () => {
    try {
      const res = await api.post('/devices', newDevice);
      setDevices(d => [...d, res.data.data]);
      setAddModal(false);
      setNewDevice({ name: '', location: '', groupName: '' });
      toast.success('Caméra ajoutée !');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur');
    }
  };

  return (
    <div>
      {showOnboarding && (
        <OnboardingWizard onComplete={() => {
          localStorage.setItem('elvyra_onboarded', '1');
          setShowOnboarding(false);
          api.get('/devices').then(r => setDevices(r.data.data || [])).catch(() => {});
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>Mes caméras</h1>
          <p style={{ color: 'var(--elvyra-text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
            Plan {user?.plan} — {devices.length} caméra(s)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/camera" style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: '8px',
            background: 'rgba(0,212,255,0.12)', border: '1px solid var(--elvyra-accent)',
            color: 'var(--elvyra-accent)', textDecoration: 'none',
            fontSize: 'var(--text-sm)', fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            📷 Mode Caméra
          </a>
          {selectedDevice && (
            <Button variant="secondary" size="sm" onClick={() => setShowRecordings(r => !r)}>
              {showRecordings ? '📹 Vue caméras' : '📼 Enregistrements'}
            </Button>
          )}
          <Button onClick={() => setAddModal(true)}>+ Ajouter</Button>
        </div>
      </div>

      {/* Audio controls pour la caméra sélectionnée */}
      {selectedDevice && (
        <div style={{ marginBottom: 12 }}>
          <AudioControls socket={socket} streamKey={selectedDevice.streamKey} deviceStatus={selectedDevice.status} />
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" style={{ width: 40, height: 40 }} /></div>
      ) : devices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--elvyra-text-muted)' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📹</div>
          <p style={{ fontSize: 'var(--text-lg)', marginBottom: 8 }}>Aucune caméra configurée</p>
          <p style={{ fontSize: 'var(--text-sm)' }}>Ajoutez votre première caméra pour commencer</p>
        </div>
      ) : showRecordings && selectedDevice ? (
        <RecordingTimeline deviceId={selectedDevice.id} deviceName={selectedDevice.name} />
      ) : (
        <GridLayout
          devices={devices}
          socket={socket}
          onFullscreen={(deviceId) => setSelectedDevice(devices.find(d => d.id === deviceId) || null)}
        />
      )}

      {addModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="card-header">
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>Nouvelle caméra</h3>
              <button onClick={() => setAddModal(false)} style={{ background: 'none', border: 'none', color: 'var(--elvyra-text-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className="form-group">
                <label className="form-label">Nom de la caméra *</label>
                <input className="form-input" placeholder="Ex: Caméra entrée" value={newDevice.name} onChange={e => setNewDevice(d => ({ ...d, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Emplacement</label>
                <input className="form-input" placeholder="Ex: Salon, Bureau..." value={newDevice.location} onChange={e => setNewDevice(d => ({ ...d, location: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Groupe</label>
                <input className="form-input" placeholder="Ex: Intérieur, Extérieur..." value={newDevice.groupName} onChange={e => setNewDevice(d => ({ ...d, groupName: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => setAddModal(false)}>Annuler</Button>
                <Button onClick={addDevice} disabled={!newDevice.name}>Ajouter</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState('timeline'); // 'timeline' | 'list'

  useEffect(() => {
    api.get('/alerts').then(r => setAlerts(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const markAllRead = async () => {
    await api.patch('/alerts/read-all');
    setAlerts(a => a.map(x => ({ ...x, isRead: true })));
    toast.success('Toutes les alertes lues');
  };

  const unreadCount = alerts.filter(a => !a.isRead).length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)' }}>Alertes</h1>
          {unreadCount > 0 && <span style={{ fontSize: 12, color: 'var(--elvyra-danger)', marginTop: 2, display: 'block' }}>{unreadCount} non lue(s)</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setView(v => v === 'timeline' ? 'list' : 'timeline')} style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--elvyra-border)', background: 'transparent', color: 'var(--elvyra-text-secondary)', cursor: 'pointer', fontSize: 13 }}>
            {view === 'timeline' ? '☰ Liste' : '⏱ Timeline'}
          </button>
          {unreadCount > 0 && (
            <Button variant="secondary" size="sm" onClick={markAllRead}>Tout marquer lu</Button>
          )}
        </div>
      </div>

      {view === 'timeline' && (
        <div style={{ padding: '0 8px' }}>
          <AlertTimeline limit={50} />
        </div>
      )}
      {view === 'list' && (loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" style={{ width: 40, height: 40 }} /></div>
      ) : alerts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--elvyra-text-muted)' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔔</div>
          <p>Aucune alerte pour le moment</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {alerts.map(alert => (
            <div key={alert.id} className="card" style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
              opacity: alert.isRead ? 0.6 : 1,
              borderLeft: alert.isRead ? '3px solid var(--elvyra-border)' : '3px solid var(--elvyra-danger)',
            }}>
              <span style={{ fontSize: 24 }}>⚠</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{alert.message}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', marginTop: 2 }}>
                  📹 {alert.device?.name} · {new Date(alert.createdAt).toLocaleString('fr-FR')}
                </p>
              </div>
              {!alert.isRead && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--elvyra-danger)', flexShrink: 0 }} />}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

